import { type BrowserHistoryArgs } from 'instantsearch.js/es/lib/routers/history'
import { type UiState } from 'instantsearch.js/es/types/ui-state'
import { useMemo } from 'react'
import { type InstantSearchNextRouting } from 'react-instantsearch-nextjs'

import { useProductSearchContext } from '../context/productSearchContextValue'

import {
  productSearchAlgoliaIndexUiStateToRoute,
  type ProductSearchAlgoliaRouteState,
} from './productSearchAlgoliaIndexUiStateToRoute'
import { productSearchAlgoliaRouteToIndexUiState } from './productSearchAlgoliaRouteToIndexUiState'

/**
 * Helper function to create a URL from the current location and route state.
 * Preserves the existing query parameters and adds the route state to it.
 */
const createUrl: BrowserHistoryArgs<ProductSearchAlgoliaRouteState>['createURL'] =
  ({ location, qsModule, routeState }) => {
    const url = new URL(location.toString())

    const qs = qsModule.parse(location.search, {
      ignoreQueryPrefix: true,
    })

    url.search = qsModule.stringify(
      {
        ...qs,
        ...routeState,
      },
      {
        addQueryPrefix: true,
      },
    )

    return url.toString()
  }

export const useProductSearchAlgoliaRouting = (): InstantSearchNextRouting<
  UiState,
  ProductSearchAlgoliaRouteState
> => {
  const {
    productSearchConfig: { baseAlgoliaConfig, sortByIndexes },
  } = useProductSearchContext()

  const { productsAlgoliaIndex } = baseAlgoliaConfig

  return useMemo(
    () => ({
      router: {
        cleanUrlOnDispose: false,
        createURL: createUrl,
      },
      stateMapping: {
        routeToState(routeState): UiState {
          return {
            [productsAlgoliaIndex]: productSearchAlgoliaRouteToIndexUiState(
              routeState,
              {
                sortByIndexes: sortByIndexes.map((item) => item.value),
              },
            ),
          }
        },
        stateToRoute(uiState): ProductSearchAlgoliaRouteState {
          return productSearchAlgoliaIndexUiStateToRoute(
            uiState[productsAlgoliaIndex],
          )
        },
      },
    }),
    [productsAlgoliaIndex, sortByIndexes],
  )
}
